import { Navigate, useLocation } from "react-router";
import {
  USER_ROLES,
  CLINIC_NAMES,
  CLINIC_ROLES,
  ROLE_INITIAL_ROUTE,
} from "static/constants";
import { checkIsAuthenticated } from "./auth";

/**
 * The user's initial page by role.
 * @returns {string} The initial page path
 */
export const getRoleInitialRoute = () => {
  const userRole = localStorage.getItem("userRole");
  return ROLE_INITIAL_ROUTE[userRole] || "/transmissions";
};

/**
 * Get the appropriate redirect path for an authenticated user
 * @returns {string} The redirect path
 */
const getAuthenticatedRedirectPath = () => {
  const previousPath = localStorage.getItem("previousPath");
  if (previousPath) {
    localStorage.removeItem("previousPath");
    return previousPath;
  }

  return getRoleInitialRoute();
};

export const getRedirectPath = () => {
  const isAuthenticated = checkIsAuthenticated();

  if (!isAuthenticated) return "/login";

  return getAuthenticatedRedirectPath();
};

export const PublicRoute = ({ component: Component, ...rest }) => {
  const isAuthenticated = checkIsAuthenticated();

  if (isAuthenticated) {
    return <Navigate to={getAuthenticatedRedirectPath()} replace />;
  }

  return <Component {...rest} />;
};

export const ProtectedRoute = ({
  authorizedRoles = [],
  component: Component,
  ...rest
}) => {
  const { pathname } = useLocation();

  const isAuthenticated = checkIsAuthenticated();
  if (!isAuthenticated) {
    localStorage.setItem("previousPath", pathname);
    return <Navigate to="/login" replace />;
  }

  const userRole = localStorage.getItem("userRole");
  const primaryRole = localStorage.getItem("primaryRole");
  const clinicName = localStorage.getItem("clinicName");
  const cliniiUserId = localStorage.getItem("clinii_user_id");
  const hasRPMDevices = localStorage.getItem("hasRPMDevices") === "true";

  const isClinicRole = CLINIC_ROLES.includes(userRole);
  const isPhysician = userRole === USER_ROLES.PHYSICIAN;
  const isCentraClinic = clinicName === CLINIC_NAMES.CENTRA;
  const isClinicWithRPMDevices = isClinicRole && hasRPMDevices;
  const isPrimaryPhysician = primaryRole === USER_ROLES.PHYSICIAN;

  /**
   * Function checks if current user role can access the route
   * @link https://docs.google.com/spreadsheets/d/16RVYJs4_Zgm058cbRtynZq-_VWWiRIl8spm7lH9jDSo/edit?usp=sharing
   * @returns {boolean} whether a user has access to the route
   */
  const checkAccess = () => {
    if (!authorizedRoles || !authorizedRoles?.length) return true;
    if (!authorizedRoles.includes(userRole)) return false;

    // Path-specific conditions
    const pathConditions = {
      "/rpm": Boolean(cliniiUserId),
      "/patient/rpm": isClinicRole ? isClinicWithRPMDevices : true,
      "/reports-assignment": isClinicRole && isCentraClinic,
      "/wearables": isPrimaryPhysician,
    };

    return Object.prototype.hasOwnProperty.call(pathConditions, pathname)
      ? pathConditions[pathname]
      : true;
  };

  const hasAccess = checkAccess();

  if (!hasAccess) {
    return <Navigate to="/prohibited" replace />;
  }

  // Clinic user redirection
  if (isClinicRole && pathname === "/transmissions") {
    const clinicRedirectPath = isPhysician
      ? "/transmissions/clinic-review"
      : "/transmissions/clinic";

    return <Navigate to={clinicRedirectPath} replace />;
  }

  return <Component {...rest} />;
};
